import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, register as apiRegister, getMe, refreshToken as apiRefreshToken } from '../lib/api';

const AuthContext = createContext(null);

// Token refresh interval (refresh every 12 hours to stay ahead of expiry)
const TOKEN_REFRESH_INTERVAL = 12 * 60 * 60 * 1000; // 12 hours in ms

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState(null);
  const [sessionError, setSessionError] = useState(null);

  // Refresh token function
  const doRefreshToken = useCallback(async () => {
    try {
      const res = await apiRefreshToken();
      const { token, user: userData } = res.data;
      localStorage.setItem('maris_token', token);
      localStorage.setItem('maris_user', JSON.stringify(userData));
      setUser(userData);
      setSessionError(null);
      return true;
    } catch (err) {
      console.log('Token refresh failed:', err?.response?.status);
      return false;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('maris_token');
    const savedUser = localStorage.getItem('maris_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid and refresh it
      getMe().then(async (res) => {
        setUser(res.data);
        localStorage.setItem('maris_user', JSON.stringify(res.data));
        // Proactively refresh the token to extend session
        await doRefreshToken();
      }).catch((err) => {
        console.log('Session validation failed:', err?.response?.status);
        localStorage.removeItem('maris_token');
        localStorage.removeItem('maris_user');
        setUser(null);
        setSessionError('Your session has expired. Please log in again.');
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [doRefreshToken]);

  // Set up periodic token refresh
  useEffect(() => {
    if (!user) return;
    
    const refreshInterval = setInterval(() => {
      doRefreshToken();
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [user, doRefreshToken]);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    const { token, user: userData } = res.data;
    localStorage.setItem('maris_token', token);
    localStorage.setItem('maris_user', JSON.stringify(userData));
    setUser(userData);
    setSessionError(null);
    return userData;
  };

  const register = async (name, email, password, role = 'OPERATOR') => {
    const res = await apiRegister(name, email, password, role);
    const { token, user: userData } = res.data;
    localStorage.setItem('maris_token', token);
    localStorage.setItem('maris_user', JSON.stringify(userData));
    setUser(userData);
    setSessionError(null);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('maris_token');
    localStorage.removeItem('maris_user');
    setUser(null);
    setSessionError(null);
  };

  const clearSessionError = () => setSessionError(null);

  const isAdmin = user?.role === 'ADMIN';
  const isDemo = user?.role === 'DEMO';

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      isDemo,
      redirectPath,
      setRedirectPath,
      sessionError,
      clearSessionError,
      refreshToken: doRefreshToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
